import { useMessages } from '@/i18n'
import { messages } from './messages'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react'
import { cn } from '@teach/ui'
import { useQuickToolbar, useToolbarBelow, useToolbarFreeze } from './quickToolbarContext'

/** Кнопка быстрой панели над элементом. */
export const QBtn = ({
  active = false,
  disabled = false,
  title,
  nativeTitle = false,
  onClick,
  onHover,
  children,
}: {
  active?: boolean
  disabled?: boolean
  title: string
  /** Системная подсказка браузера. Выключена по умолчанию: заказчик снял подсказки
   *  на наведении — кнопки и так подписаны или понятны по иконке. */
  nativeTitle?: boolean
  onClick: () => void
  /** Наведение настоящим движением мыши (см. `QDropdown`). */
  onHover?: () => void
  children: ReactNode
}) => (
  <button
    type="button"
    title={nativeTitle ? title : undefined}
    // Только РЕАЛЬНОЕ движение (`movementX/Y`): примерка меняет высоту элемента и
    // сдвигает список под неподвижной мышью — браузер шлёт на сдвиге «наведение»
    // без движения, и пункты переключались бы по кругу тысячи раз.
    onPointerMove={
      onHover
        ? (e) => {
            if (e.movementX !== 0 || e.movementY !== 0) onHover()
          }
        : undefined
    }
    aria-label={title}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
      active ? 'bg-primary text-primary-foreground' : 'hover:bg-foreground/10',
    )}
  >
    {children}
  </button>
)

/** Разделитель групп. */
export const QSep = () => <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />

/** «− значение +» — наклон, число строк и столбцов. */
export const QStepper = ({
  label,
  value,
  display,
  min,
  max,
  onChange,
}: {
  label: string
  value: number
  display: string
  min: number
  max: number
  onChange: (next: number) => void
}) => {
  const m = useMessages(messages)
  return (
    <div className="flex items-center" aria-label={label}>
      <QBtn title={m.less(label)} disabled={value <= min} onClick={() => onChange(value - 1)}>
        <Minus className="size-3.5" />
      </QBtn>
      <span className="min-w-9 text-center text-xs font-semibold tabular-nums" title={label}>
        {display}
      </span>
      <QBtn title={m.more(label)} disabled={value >= max} onClick={() => onChange(value + 1)}>
        <Plus className="size-3.5" />
      </QBtn>
    </div>
  )
}

/** Раскрывашка: кнопка с текущим значением. Список уходит в сторону от элемента
 *  (вниз, если панель под ним, иначе вверх), наведение на пункт — примерка. */
export const QDropdown = <V extends string>({
  title,
  value,
  options,
  onChange,
  onPreview,
}: {
  title: string
  value: V
  options: Array<{ value: V; label: string }>
  onChange: (next: V) => void
  /** Примерка пункта под мышью; `null` — вернуть как было. */
  onPreview?: (next: V | null) => void
}) => {
  const below = useToolbarBelow()
  const freeze = useToolbarFreeze()
  const [open, setOpen] = useState(false)
  // Пока список открыт — панель стоит на месте, что бы ни делала примерка с элементом.
  useEffect(() => {
    freeze(open)
    return () => freeze(false)
  }, [open, freeze])
  const current = options.find((o) => o.value === value) ?? options[0]
  const close = () => {
    setOpen(false)
    onPreview?.(null)
  }
  const closeRef = useRef(close)
  closeRef.current = close
  // Закрывается кликом мимо, а не уходом мыши: между кнопкой и списком зазор,
  // и по дороге к пункту список схлопывался бы.
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) closeRef.current()
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])
  // Список раскрывается ВСЕГДА от элемента — на текст он не ложится никогда.
  // Если столбиком в ту сторону не помещается (у верха холста — полоска
  // прогресса), варианты встают в одну строку, сдвинутую так, чтобы не вылезти
  // за края холста.
  const menuRef = useRef<HTMLDivElement>(null)
  const [row, setRow] = useState(false)
  const [shift, setShift] = useState(0)
  useLayoutEffect(() => {
    if (!open) {
      setRow(false)
      setShift(0)
      return
    }
    const menu = menuRef.current?.getBoundingClientRect()
    const canvas = ref.current?.closest('[data-design-canvas]')?.getBoundingClientRect()
    if (!menu || !canvas) return
    if (!row) {
      if (below ? menu.bottom > canvas.bottom : menu.top < canvas.top) setRow(true)
      return
    }
    const pad = 8
    const left = menu.left - shift
    const right = menu.right - shift
    setShift(
      left < canvas.left + pad
        ? canvas.left + pad - left
        : right > canvas.right - pad
          ? canvas.right - pad - right
          : 0,
    )
    // `shift` читается для пересчёта от исходного места, а не триггерит эффект.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, below, row])
  const down = below
  const Chevron = down ? ChevronDown : ChevronUp
  return (
    <div ref={ref} className="relative">
      <QBtn title={title} active={open} onClick={() => (open ? close() : setOpen(true))}>
        {current.label}
        <Chevron className="ml-1 size-3.5 opacity-70" />
      </QBtn>
      {open ? (
        <div
          ref={menuRef}
          onMouseLeave={() => onPreview?.(null)}
          style={shift ? { marginLeft: shift } : undefined}
          className={cn(
            'absolute left-1/2 z-50 flex min-w-full -translate-x-1/2 gap-0.5 rounded-xl bg-popover p-1 text-popover-foreground shadow-xl ring-1 ring-border duration-150 animate-in fade-in',
            row ? 'flex-row whitespace-nowrap' : 'flex-col',
            down ? 'top-full mt-1 slide-in-from-top-1' : 'bottom-full mb-1 slide-in-from-bottom-1',
          )}
        >
          {options.map((o) => (
            <QBtn
              key={o.value}
              title={o.label}
              nativeTitle={false}
              active={o.value === value}
              onHover={() => onPreview?.(o.value)}
              onClick={() => {
                onPreview?.(null)
                onChange(o.value)
                setOpen(false)
              }}
            >
              {o.label}
            </QBtn>
          ))}
        </div>
      ) : null}
    </div>
  )
}
